import * as React from 'react';
import { z } from 'zod';
import { FormLayout, ReadOnlyField } from './Field';
import { FormField, type FormSchema, type ZodForm } from './Form';
import { Input } from './Input';
import { Grid, Stack } from './Layout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './Select';

export interface InvoiceFormFieldsCustomer {
  id: string;
  accountName: string;
}

/**
 * The invoice's own facts, spread into the editor's schema.
 *
 * An invoice is billed to an account on a date; the rest of this block —
 * the number, the linked call, the vehicle — is reference data that an
 * invoice can go out without. The number is checked for uniqueness by the
 * container, which is the only party that knows the numbers already taken.
 */
export const invoiceDetailsShape = {
  /** The selected customer's id. */
  customer: z.string().min(1, 'Choose the account to bill.'),
  invoiceNumber: z.string(),
  /** ISO date strings, as a date input holds them. */
  serviceDate: z.string().min(1, 'Pick the service date.'),
  /** Derived from the payment terms; shown, never edited. */
  dueDate: z.string(),
  serviceCall: z.string(),
  vehicleMake: z.string(),
  licensePlate: z.string(),
  vin: z.string(),
};

export interface InvoiceFormFieldsViewProps<S extends FormSchema> {
  /** The editor's form; its schema spreads `invoiceDetailsShape`. */
  form: ZodForm<S>;
  customers?: InvoiceFormFieldsCustomer[];
  /** Runs when the invoice number field blurs. */
  validateInvoiceNumber?: (value: string) => void;
  /** An existing invoice keeps its number. */
  isInvoiceNumberReadOnly?: boolean;
  /**
   * The "Service call #" control. It looks calls up by company, so the
   * container renders it and passes it in.
   */
  serviceCallField: React.ReactNode;
  /** A linked call supplies the date and vehicle, so those fields lock. */
  isCallSelected?: boolean;
  /** The account shows as a read-only value and nothing can be edited. */
  isReadOnly?: boolean;
}

/** The shape as a schema, for the cast below. */
type InvoiceDetailsSchema = z.ZodObject<typeof invoiceDetailsShape>;

/**
 * The top of the invoice form: account and number, then the dates and the
 * service call, then the vehicle.
 *
 *   <InvoiceFormFieldsView form={form} customers={accounts}
 *     serviceCallField={<ServiceCallComboBox … />} />
 *
 * Which fields are required comes from the schema, so the markers and the
 * messages agree with what the submit actually refuses.
 */
export function InvoiceFormFieldsView<S extends FormSchema>({
  form,
  customers = [],
  validateInvoiceNumber,
  isInvoiceNumberReadOnly = false,
  serviceCallField,
  isCallSelected = false,
  isReadOnly = false,
}: InvoiceFormFieldsViewProps<S>) {
  // See LotFormFields: the caller's schema spreads this shape, which
  // TypeScript cannot see through the generic.
  const f = form as unknown as ZodForm<InvoiceDetailsSchema>;
  const customer = f.watch('customer');
  /* A sent invoice is read back here, and nothing on a page you cannot type
     into is required of you. */
  const marker = isReadOnly ? false : undefined;
  const selectedCustomerName = customers.find(
    (c) => c.id === customer
  )?.accountName;

  return (
    <Stack gap={6}>
      <Grid cols={{ base: 1, md: 2 }} gap={6}>
        <FormField form={f} name="customer" label="Account" required={marker}>
          {(field) =>
            isReadOnly && field.value ? (
              <ReadOnlyField value={selectedCustomerName || field.value} />
            ) : (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isReadOnly}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                {!isReadOnly && (
                  <SelectContent>
                    {customers.map((cust) => (
                      <SelectItem key={cust.id} value={cust.id}>
                        {cust.accountName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                )}
              </Select>
            )
          }
        </FormField>
        <FormField form={f} name="invoiceNumber" label="Invoice number">
          {(field) => (
            <Input
              {...field}
              onBlur={() => {
                field.onBlur();
                validateInvoiceNumber?.(field.value);
              }}
              placeholder="Auto generated"
              readOnly={isInvoiceNumberReadOnly}
              disabled={isInvoiceNumberReadOnly}
            />
          )}
        </FormField>
      </Grid>

      <FormLayout columns={3} gap="relaxed">
        <FormField
          form={f}
          name="serviceDate"
          label="Service date"
          required={marker}
        >
          {(field) => (
            <Input type="date" {...field} disabled={isCallSelected} />
          )}
        </FormField>
        <FormField form={f} name="dueDate" label="Due date">
          {(field) => <Input type="date" {...field} readOnly disabled />}
        </FormField>
        <FormField form={f} name="serviceCall" label="Service call #">
          {serviceCallField}
        </FormField>
      </FormLayout>

      <FormLayout columns={3} gap="relaxed">
        <FormField form={f} name="vehicleMake" label="Vehicle make/model">
          {(field) => <Input {...field} disabled={isCallSelected} />}
        </FormField>
        <FormField form={f} name="licensePlate" label="License plate">
          {(field) => <Input {...field} disabled={isCallSelected} />}
        </FormField>
        <FormField form={f} name="vin" label="VIN (last 4)">
          {(field) => <Input {...field} disabled={isCallSelected} />}
        </FormField>
      </FormLayout>
    </Stack>
  );
}
